/* eslint-disable react/prop-types */
/* eslint-disable no-negated-condition */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { GeoJsonLayer } from '@deck.gl/layers';
import { WebMercatorViewport } from '@deck.gl/core';

type SelectedPayload = {
	id?: string;
	title?: string;
	codigoDistrito?: string;
	codigoMunicipio?: string;
	nombreMunicipio?: string;
};

type Props = {
	readonly className?: string;
	readonly height?: number | string;
	readonly hasAutoZoom?: boolean;
	readonly onHome?: () => void;
	readonly onMapObjectClick?: (p: SelectedPayload) => void;
	readonly onSelectChange?: (p: SelectedPayload) => void;
};

const GEO_BASE = '/colegio-madrid/geo';
const INITIAL_VIEW = { longitude: -3.7038, latitude: 40.4168, zoom: 9, bearing: 0, pitch: 0 };
type Level = 'districts' | 'municipalities';

/** Paleta discreta con buen contraste (A=180 para suavizar relleno) */
const PALETTE: Array<[number, number, number, number]> = [
	[33, 150, 243, 180],
	[76, 175, 80, 180],
	[255, 193, 7, 180],
	[244, 67, 54, 180],
	[156, 39, 176, 180],
	[0, 188, 212, 180],
	[121, 85, 72, 180],
	[63, 81, 181, 180],
	[205, 220, 57, 180],
	[255, 87, 34, 180],
	[124, 179, 66, 180],
	[0, 150, 136, 180],
];
const hashToIdx = (s: string | number) =>
	Math.abs(
		String(s)
			.split('')
			.reduce((a, c) => a * 31 + (c.charCodeAt(0) || 0), 7),
	) % PALETTE.length;

/** ---- Utils ---- */
function bboxFromGeoJSON(gj: any) {
	if (!gj || gj.type !== 'FeatureCollection' || !Array.isArray(gj.features)) return null;
	let minLon = Infinity;
	let minLat = Infinity;
	let maxLon = -Infinity;
	let maxLat = -Infinity;
	const walk = (c: any) => {
		if (!c) return;
		if (typeof c[0] === 'number') {
			const [lon, lat] = c as [number, number]; // WGS84 (lon,lat)
			if (lon < minLon) minLon = lon;
			if (lon > maxLon) maxLon = lon;
			if (lat < minLat) minLat = lat;
			if (lat > maxLat) maxLat = lat;
			return;
		}
		for (const v of c) walk(v);
	};
	for (const f of gj.features) walk(f?.geometry?.coordinates);
	if ([minLon, minLat, maxLon, maxLat].some((v) => !Number.isFinite(v))) return null;
	return [
		[minLon, minLat],
		[maxLon, maxLat],
	] as [[number, number], [number, number]];
}

function getCodigoDistrito(props: any, fallbackId?: string | number | null) {
	return (
		props?.codigoDistrito ??
		props?.codigo_distrito ??
		props?.CODIGODISTRITO ??
		props?.CODIGO_DIST ??
		(typeof fallbackId !== 'undefined' ? String(fallbackId) : null)
	);
}

function getCodigoMunicipio(props: any, fallbackId?: string | number | null) {
	return (
		props?.codigoIneMunicipio ??
		props?.CODIGOINE ??
		props?.cod_ine ??
		props?.COD_INE ??
		(typeof fallbackId !== 'undefined' ? String(fallbackId) : null)
	);
}

/** Normaliza distritos (id / name estables) */
function normalizeDistricts(gj: any) {
	if (!gj?.features) return gj;
	return {
		...gj,
		features: gj.features.map((f: any) => {
			const p = { ...(f.properties ?? {}) };
			const idCandidate = getCodigoDistrito(p, f.id ?? p.id);
			const id = idCandidate ? String(idCandidate) : undefined;
			const name = p.name ?? p.NOMBRE ?? p.NAME ?? (id ? `Distrito ${id}` : 'Distrito');
			return { ...f, id, properties: { ...p, name, codigoDistrito: id } };
		}),
	};
}

/** Normaliza municipios (id / name estables) */
function normalizeMunicipios(gj: any) {
	if (!gj?.features) return gj;
	return {
		...gj,
		features: gj.features.map((f: any) => {
			const p = { ...(f.properties ?? {}) };
			const id = String(getCodigoMunicipio(p, f.id ?? p.id) ?? '');
			const name = p.name ?? p.descIneMunicipio ?? p.NOMBRE ?? p.NAME ?? id;
			return { ...f, id, properties: { ...p, name } };
		}),
	};
}

export const MadridMap: React.FC<Props> = ({
	className,
	height = 500,
	hasAutoZoom = true,
	onHome,
	onMapObjectClick,
	onSelectChange,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [viewState, setViewState] = useState(INITIAL_VIEW);
	const [data, setData] = useState<any>(null);
	const [level, setLevel] = useState<Level>('districts');
	const [loading, setLoading] = useState(false);
	const [selectedDistrito, setSelectedDistrito] = useState<string | null>(null);
	const [selectedDistritoName, setSelectedDistritoName] = useState<string | null>(null);
	const [hover, setHover] = useState<{ x: number; y: number; text: string } | null>(null);

	// Estado para mostrar debajo del mapa
	const [selection, setSelection] = useState<{
		distrito?: string;
		nombreDistrito?: string;
		municipio?: string;
		nombre?: string;
	} | null>(null);

	const fitTo = useCallback(
		(gj: any) => {
			if (!hasAutoZoom || !containerRef.current) return;
			const bounds = bboxFromGeoJSON(gj);
			if (!bounds) return;
			const rect = containerRef.current.getBoundingClientRect();
			const vp = new WebMercatorViewport({ width: rect.width || 800, height: rect.height || 500 });
			const { longitude, latitude, zoom } = vp.fitBounds(bounds, { padding: 32 });
			setViewState((vs) => ({ ...vs, longitude, latitude, zoom }));
		},
		[hasAutoZoom],
	);

	const loadDistricts = useCallback(async () => {
		setLoading(true);
		try {
			const res = await fetch(`${GEO_BASE}/distritos-madrid.geojson`);
			const raw = await res.json();
			const gj = normalizeDistricts(raw);
			setData(gj);
			setLevel('districts');
			setSelectedDistrito(null);
			setSelectedDistritoName(null);
			setSelection(null);
			fitTo(gj);
			onHome?.();
		} finally {
			setLoading(false);
		}
	}, [fitTo, onHome]);

	const loadMunicipios = useCallback(
		async (codigoDistrito: string, distritoName?: string) => {
			setLoading(true);
			try {
				const url = `${GEO_BASE}/municipios-${codigoDistrito}.geojson`;
				const res = await fetch(url);
				if (!res.ok) {
					console.warn(`No puedo cargar ${url} (HTTP ${res.status})`);
					return;
				}
				const raw = await res.json();
				const gj = normalizeMunicipios(raw);
				setData(gj);
				setLevel('municipalities');
				setSelectedDistrito(codigoDistrito);
				setSelectedDistritoName(distritoName ?? null);
				setSelection({ distrito: codigoDistrito, nombreDistrito: distritoName ?? undefined });
				fitTo(gj);

				const payload = {
					id: codigoDistrito,
					title: distritoName ?? `Distrito ${codigoDistrito}`,
					codigoDistrito,
				};
				onMapObjectClick?.(payload);
				onSelectChange?.(payload);
			} finally {
				setLoading(false);
			}
		},
		[fitTo, onMapObjectClick, onSelectChange],
	);

	useEffect(() => {
		loadDistricts();
	}, [loadDistricts]);

	const layers = useMemo(() => {
		if (!data) return [];

		return [
			new GeoJsonLayer({
				id: level === 'districts' ? 'distritos' : 'municipios',
				data,
				pickable: true,
				stroked: true,
				filled: true,
				autoHighlight: true,
				getLineColor: [255, 255, 255, 255],
				getLineWidth: 1,
				lineWidthMinPixels: 1,
				getFillColor(f: any) {
					const props = f?.properties ?? {};
					const code =
						level === 'districts'
							? (getCodigoDistrito(props, f.id) ?? '')
							: (getCodigoMunicipio(props, f.id) ?? '');
					return PALETTE[hashToIdx(code)];
				},
				onClick({ object }: any) {
					if (!object) return;
					const props = object.properties ?? {};
					const name = props.name ?? props.NAME ?? '';

					if (level === 'districts') {
						const codigoDistrito = getCodigoDistrito(props, object.id);
						if (!codigoDistrito) {
							console.warn('No se encontró codigoDistrito en el feature clicado:', props);
							return;
						}
						loadMunicipios(String(codigoDistrito), name || undefined);
					} else {
						// Click en municipio
						const muniCode = getCodigoMunicipio(props, object.id);
						const distritoCode = selectedDistrito ?? undefined;
						const name = props.name ?? props.NAME ?? '';

						setSelection({
							distrito: distritoCode,
							nombreDistrito: selectedDistritoName ?? undefined,
							municipio: muniCode ? String(muniCode) : undefined,
							nombre: name || undefined,
						});

						const payload: SelectedPayload = {
							id: object.id ?? props.id,
							title: name,
							codigoDistrito: distritoCode,
							codigoMunicipio: muniCode ? String(muniCode) : undefined, // <-- NUEVO
							nombreMunicipio: name || undefined,
						};
						onMapObjectClick?.(payload);
						onSelectChange?.(payload);
					}
				},
				updateTriggers: { getFillColor: [data, level] },
			}),
		];
	}, [data, level, loadMunicipios, onMapObjectClick, onSelectChange, selectedDistrito, selectedDistritoName]);

	return (
		<>
			<div
				ref={containerRef}
				className={className}
				style={{
					width: '100%',
					height: typeof height === 'number' ? `${height}px` : height,
					position: 'relative',
				}}
			>
				{level === 'municipalities' && (
					<button
						type="button"
						style={{
							position: 'absolute',
							top: 12,
							left: 12,
							zIndex: 10,
							padding: '6px 10px',
							borderRadius: 8,
							border: '1px solid #ccc',
							background: '#fff',
							cursor: loading ? 'not-allowed' : 'pointer',
							opacity: loading ? 0.7 : 1,
						}}
						disabled={loading}
						onClick={loadDistricts}
					>
						{loading ? 'Cargando…' : 'Volver a distritos'}
					</button>
				)}

				<DeckGL
					controller
					layers={layers}
					initialViewState={INITIAL_VIEW}
					viewState={viewState}
					getCursor={({ isDragging, isHovering }) =>
						isDragging ? 'grabbing' : isHovering ? 'pointer' : 'grab'
					}
					style={{ width: '100%', height: '100%', background: '#eef3f6' }}
					onHover={({ x, y, object }: any) => {
						if (!object) {
							setHover(null);
							return;
						}
						const p = object.properties ?? {};
						const name = p.name ?? p.NAME ?? '';
						setHover({ x, y, text: name });
					}}
					onViewStateChange={(e: any) => setViewState(e.viewState)}
				/>

				{hover && (
					<div
						style={{
							position: 'absolute',
							left: hover.x,
							top: Math.max(8, hover.y - 18),
							transform: 'translate(-50%, -100%)',
							pointerEvents: 'none',
							background: 'rgba(0,0,0,0.8)',
							color: '#fff',
							padding: '6px 8px',
							borderRadius: 6,
							fontSize: 12,
							lineHeight: 1.2,
							boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
							zIndex: 20,
						}}
					>
						{hover.text}
					</div>
				)}
			</div>

			{/* Panel con selección actual */}
			<div style={{ marginTop: 12, fontFamily: 'system-ui, sans-serif', fontSize: 14 }}>
				<strong>Selección:</strong>{' '}
				{selection ? (
					<>
						{selection.distrito ? (
							<>Distrito: {selection.nombreDistrito ? <em>{selection.nombreDistrito}</em> : null} · </>
						) : null}
						{selection.municipio ? <>Municipio: </> : null}
						{selection.nombre ? <em>{selection.nombre}</em> : null}
					</>
				) : (
					<em>ninguna</em>
				)}
			</div>
		</>
	);
};
